import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { cn } from '../utils/cn.js';
const PADDING = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
};
// Literal class names so Tailwind's content scanner keeps them.
const RAIL = {
    left: {
        forest: 'border-l-4 border-l-forest',
        clay: 'border-l-4 border-l-clay',
        gold: 'border-l-4 border-l-gold',
        leaf: 'border-l-4 border-l-leaf',
        sun: 'border-l-4 border-l-sun',
        danger: 'border-l-4 border-l-danger',
    },
    top: {
        forest: 'border-t-4 border-t-forest',
        clay: 'border-t-4 border-t-clay',
        gold: 'border-t-4 border-t-gold',
        leaf: 'border-t-4 border-t-leaf',
        sun: 'border-t-4 border-t-sun',
        danger: 'border-t-4 border-t-danger',
    },
};
/**
 * Surface card — a white paper panel that layers on the warm cream page,
 * bounded by a hairline border (Principle 6 — borders for depth, never
 * shadows; no `shadow-*` utility anywhere).
 *
 * ACCENT-RAIL CONVENTION (keep consistent across the app):
 *   • A SOLO / standalone card        → NO rail (plain white).
 *   • Cards STACKED vertically (2+)   → accent="left"
 *   • Cards in a HORIZONTAL row/grid  → accent="top"
 * Rails mark distinct sections WITHIN a multi-card group; a lone card has
 * nothing to differentiate from, so it stays plain. Decorate a group
 * all-or-none — never "first card railed, the rest plain".
 * `tone` categorises by meaning: forest = primary/identity, gold = money/
 * billing, clay = security/access, leaf = compliance, sun/danger = status.
 */
export const Card = forwardRef(function Card({ padding = 'md', accent, tone = 'forest', flat: _flat = false, className, children, ...rest }, ref) {
    return (_jsx("div", { ref: ref, className: cn('rounded-lg border border-border bg-paper', accent ? RAIL[accent][tone] : '', PADDING[padding], className), ...rest, children: children }));
});
const TAG_TONE = {
    forest: 'text-forest',
    clay: 'text-clay',
    gold: 'text-gold',
    muted: 'text-ink-muted',
};
/** Card header — title (serif), optional title-line tag + trailing action, and
 *  a description line. Sits at the top of a <Card>. */
export function CardHeader({ title, description, tag, tagTone = 'forest', action, className, ...rest }) {
    return (_jsxs("div", { className: cn('mb-4', className), ...rest, children: [_jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-x-3 gap-y-1", children: [_jsx("h2", { className: "font-heading text-xl leading-tight text-ink [text-wrap:balance]", children: title }), tag ? (_jsx("span", { className: cn('inline-block rounded-full border border-border bg-paper-warm px-2 py-0.5 font-body text-[0.62rem] font-semibold uppercase tracking-[0.12em]', TAG_TONE[tagTone]), children: tag })) : null] }), action ? _jsx("div", { className: "shrink-0", children: action }) : null] }), description ? (_jsx("p", { className: "mt-1.5 font-body text-sm leading-relaxed text-ink-muted", children: description })) : null] }));
}
//# sourceMappingURL=Card.js.map